/**
 * KAN-843 — "open this customer" hand-off from another screen.
 *
 * A screen outside Customers (the Schedule stop pop-up, the AI assistant)
 * calls `requestCustomerOpen(id)` and then moves to the Customers tab; the
 * Customers owner calls `consumePendingCustomerOpen()` once its list has
 * loaded and opens that customer's screen when the id is in ITS OWN loaded
 * (RLS-scoped) list. The id never goes into the URL (RD-NAV-002) and never
 * grants access: an id the list does not hold is simply dropped.
 *
 * A module-level slot, not a context, so no role home grows a provider.
 * One pending request at a time (the latest wins); it expires after
 * MAX_AGE_MS so a request that never reached Customers cannot fire later.
 * Holds an id + timestamp only; cleared on consume and at sign-out
 * (`clearPendingCustomerOpen`).
 */
export const MAX_AGE_MS = 15000;

let pending = null;
const listeners = new Set();

export function requestCustomerOpen(customerId, now = Date.now()) {
  if (typeof customerId !== 'string' || !customerId) return;
  pending = { id: customerId, at: now };
  // Wakes an owner that is ALREADY on Customers (no tab change to re-run its effect).
  listeners.forEach((fn) => fn());
}

/** Owner side: called after every request. Returns an unsubscribe. */
export function subscribePendingCustomerOpen(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Returns the pending customer id (and clears it), or null when none / expired. */
export function consumePendingCustomerOpen(now = Date.now()) {
  const p = pending;
  pending = null;
  if (!p || now - p.at > MAX_AGE_MS) return null;
  return p.id;
}

/** Read without clearing — lets an owner wait for its list to load. */
export function hasPendingCustomerOpen(now = Date.now()) {
  return !!pending && now - pending.at <= MAX_AGE_MS;
}

export function clearPendingCustomerOpen() {
  pending = null;
}
